/* src/util/ChemDrawUtils.js */
import { CHEMDRAW } from "../constants/labels";

/**
 * Render each SMILES into an **SVG** data‑URL (fallback: high‑DPI PNG).
 * @param {string[]} smilesList
 * @param {object}   opts              – optional { size, pngDpi }
 *        opts.size   : int  – size of the canvas square in px (default = 240)
 *        opts.pngDpi : int  – DPI for the PNG fallback   (default = 300)
 * @returns {Promise<string[]>} array of data‑URLs (one per SMILES, null on error)
 */
export async function generateImagesFromSmiles(smilesList, opts = {}) {
  const SIZE     = opts.size   ?? 240;   // 240 × 240 px SVG viewport
  const PNG_DPI  = opts.pngDpi ?? 300;   // hi‑res fallback

  /* 1 – create an invisible host div */
  const containerId  = "chemdraw_container_" + Date.now();
  const container    = document.createElement("div");
  container.id       = containerId;
  container.style.cssText = "position:fixed;left:-9999px;top:-9999px;";
  document.body.appendChild(container);

  /* 2 – attach ChemDraw */
  const cd = await new Promise((resolve, reject) => {
    window.RevvitySignals.ChemdrawWebManager.attach({
      id:        containerId,
      licenseUrl: CHEMDRAW.LICENSE_URL,
      callback:  inst => inst ? resolve(inst) : reject(new Error("ChemDraw attach failed")),
      config: {
        features   : { enabled: ["WebService"] },
        properties : { chemservice: CHEMDRAW.CHEM_SERVICE_URL },
      },
    });
  });

  /* 3 – render everything */
  const out = [];

  for (const smiles of smilesList) {
    await new Promise(res => {
      cd.clear();
      cd.loadSMILES(smiles, (cdxml, err) => {
        if (err) {
          console.error(`ChemDraw – SMILES “${smiles}” failed:`, err);
          out.push(null);
          return res();
        }

        /* --- first choice: scalable SVG ---------------------------------- */
        try {
          const svgUrl = cd.getImgUrl({
            format     : "svg",
            width      : SIZE,
            height     : SIZE,
            borderSize : 0,
          });
          out.push(svgUrl);
          return res();
        } catch (e) {
          /* some on‑prem releases didn’t yet support SVG export – fallback */
        }

        /* --- fallback: high‑DPI PNG -------------------------------------- */
        try {
          const pngUrl = cd.getImgUrl({
            format     : "png",
            dpi        : PNG_DPI,   // makes the raster image crisp even when
            borderSize : 0,         // displayed at 240 px on screen
          });
          out.push(pngUrl);
        } catch (e) {
          console.error("ChemDraw export failed:", e);
          out.push(null);
        }
        res();
      });
    });
  }

  /* 4 – tidy up */
  document.body.removeChild(container);
  return out;
}
